import { useState, useEffect } from "react";
import { 
  Typography, 
  Box, 
  TextField, 
  Select, 
  MenuItem, 
  IconButton, 
  Button,
  FormControl,
  InputLabel,
  InputAdornment,
  Paper,
  Alert,
  Snackbar,
  CircularProgress
} from "@mui/material";
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useLanguage } from "../../context/LanguageContext";
import { useApp } from "../../context/AppContext";
import api from "../../api/api";
import { binanceApi } from "../../services/binance.api.service";

// Импорт иконок криптовалют
import btcIcon from "../../assets/img/icons/btc.png";
import ethIcon from "../../assets/img/icons/eth.png";
import adaIcon from "../../assets/img/icons/ada.png";
import bnbIcon from "../../assets/img/icons/bnb.png";
import dogeIcon from "../../assets/img/icons/doge.png";
import ltcIcon from "../../assets/img/icons/ltc.png";
import maticIcon from "../../assets/img/icons/matic.png";
import solIcon from "../../assets/img/icons/sol.png";
import trxIcon from "../../assets/img/icons/trx.png";
import usdtIcon from "../../assets/img/icons/usdt.png";
import xmrIcon from "../../assets/img/icons/xmr.png";
import xrpIcon from "../../assets/img/icons/xrp.png";

const cryptoIcons = {
  BTC: btcIcon,
  ETH: ethIcon,
  ADA: adaIcon,
  BNB: bnbIcon,
  DOGE: dogeIcon,
  LTC: ltcIcon,
  MATIC: maticIcon,
  SOL: solIcon,
  TRX: trxIcon,
  USDT: usdtIcon,
  XMR: xmrIcon,
  XRP: xrpIcon,
};

export default function Exchange() {
  const { t } = useLanguage();
  const { wallets } = useApp();
  
  const [fromCurrency, setFromCurrency] = useState('BTC');
  const [toCurrency, setToCurrency] = useState('ETH');
  const [fromAmount, setFromAmount] = useState('0.00');
  const [toAmount, setToAmount] = useState('0.00');
  const [exchangeRate, setExchangeRate] = useState(0);
  const [fee, setFee] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [exchangeRates, setExchangeRates] = useState({});
  const [ratesLoading, setRatesLoading] = useState(true);

  // Получаем балансы кошельков
  const getWalletBalance = (currency) => {
    const wallet = wallets.find(w => w.asset_ticker === currency);
    return wallet ? parseFloat(wallet.balance) : 0;
  };

  // Получение динамических курсов с Binance
  const fetchExchangeRates = async () => {
    try {
      setRatesLoading(true);
      const prices = await binanceApi.getCryptoPrices();
      
      // Создаем объект с курсами (индексы соответствуют порядку в binance.api.service.js)
      const rates = {
        BTC: parseFloat(prices[0] || 0), // BTCUSDT
        ETH: parseFloat(prices[1] || 0), // ETHUSDT
        BNB: parseFloat(prices[2] || 0), // BNBUSDT
        SOL: parseFloat(prices[3] || 0), // SOLUSDT
        XRP: parseFloat(prices[4] || 0), // XRPUSDT
        ADA: parseFloat(prices[5] || 0), // ADAUSDT
        DOGE: parseFloat(prices[6] || 0), // DOGEUSDT
        LTC: parseFloat(prices[7] || 0), // LTCUSDT
        MATIC: parseFloat(prices[8] || 0), // MATICUSDT
        TRX: parseFloat(prices[9] || 0), // TRXUSDT
        EUR: parseFloat(prices[10] || 0), // EURUSDT
        USD: 1, // USD всегда = 1
        USDT: 1 // USDT привязан к USD
      };
      
      setExchangeRates(rates);
      console.log('Exchange rates updated:', rates);
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      setError('Failed to fetch exchange rates');
    } finally {
      setRatesLoading(false);
    }
  };

  // Получение курса обмена между двумя валютами
  const getExchangeRate = (from, to) => {
    if (!exchangeRates[from] || !exchangeRates[to]) {
      return 1; // Возвращаем 1 если курсы не загружены
    }
    
    // Если обмениваем на USD/USDT, просто возвращаем курс
    if (to === 'USD' || to === 'USDT') {
      return exchangeRates[from];
    }
    
    // Если обмениваем с USD/USDT, возвращаем обратный курс
    if (from === 'USD' || from === 'USDT') {
      return 1 / exchangeRates[to];
    }
    
    // Для обмена между криптовалютами: from_rate / to_rate
    return exchangeRates[from] / exchangeRates[to];
  };

  // Обработка изменения суммы
  const handleFromAmountChange = (value) => {
    setFromAmount(value);
    const rate = getExchangeRate(fromCurrency, toCurrency);
    const calculatedAmount = parseFloat(value) * rate;
    
    // Расчет комиссии (0.5% от суммы)
    const feeAmount = parseFloat(value) * 0.005;
    setFee(feeAmount);
    
    // Общая сумма с учетом комиссии
    const totalWithFee = calculatedAmount - (calculatedAmount * 0.005);
    setToAmount(totalWithFee.toFixed(8));
    setExchangeRate(rate);
    setTotalAmount(totalWithFee);
  };

  // Обработка изменения валюты
  const handleCurrencyChange = (type, currency) => {
    if (type === 'from') {
      setFromCurrency(currency);
    } else {
      setToCurrency(currency);
    }
    
    // Пересчитываем курс
    const rate = getExchangeRate(
      type === 'from' ? currency : fromCurrency,
      type === 'to' ? currency : toCurrency
    );
    setExchangeRate(rate);
    
    // Пересчитываем сумму
    if (fromAmount && fromAmount !== '0.00') {
      const calculatedAmount = parseFloat(fromAmount) * rate;
      const feeAmount = parseFloat(fromAmount) * 0.005;
      const totalWithFee = calculatedAmount - (calculatedAmount * 0.005);
      setToAmount(totalWithFee.toFixed(8));
      setTotalAmount(totalWithFee);
      setFee(feeAmount);
    }
  };

  // Функция обмена
  const handleSwap = async () => {
    // Валидация
    if (parseFloat(fromAmount) <= 0) {
      setError('Please enter a valid amount');
      setShowAlert(true);
      return;
    }
    
    const fromBalance = getWalletBalance(fromCurrency);
    if (parseFloat(fromAmount) > fromBalance) {
      setError(`Insufficient balance. Available: ${fromBalance.toFixed(8)} ${fromCurrency}`);
      setShowAlert(true);
      return;
    }
    
    if (fromCurrency === toCurrency) {
      setError('Cannot exchange the same currency');
      setShowAlert(true);
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await api.post('/exchange', {
        asset_from: fromCurrency,
        amount_from: parseFloat(fromAmount),
        asset_to: toCurrency,
        amount_to: parseFloat(toAmount)
      });
      
      if (response.data.success) {
        setSuccess('Exchange completed successfully!');
        setShowAlert(true);
        // Сброс полей после успешного обмена
        setFromAmount('0.00');
        setToAmount('0.00');
        setFee(0);
        setTotalAmount(0);
      } else {
        setError(response.data.message || 'Exchange failed');
        setShowAlert(true);
      }
    } catch (err) {
      console.error('Exchange error:', err);
      setError(err.response?.data?.message || 'Exchange failed. Please try again.');
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Функция смены валют местами
  const handleSwapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setFromAmount(toAmount);
    setToAmount(fromAmount);
  };

  // Загрузка курсов при монтировании компонента и периодическое обновление
  useEffect(() => {
    fetchExchangeRates();
    
    // Обновляем курсы каждые 30 секунд
    const interval = setInterval(fetchExchangeRates, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Пересчет суммы при изменении курсов
  useEffect(() => {
    if (!ratesLoading && fromAmount && fromAmount !== '0.00') {
      const rate = getExchangeRate(fromCurrency, toCurrency);
      const calculatedAmount = parseFloat(fromAmount) * rate;
      const feeAmount = parseFloat(fromAmount) * 0.005;
      const totalWithFee = calculatedAmount - (calculatedAmount * 0.005);
      setToAmount(totalWithFee.toFixed(8));
      setTotalAmount(totalWithFee);
      setFee(feeAmount);
      setExchangeRate(rate);
    }
  }, [exchangeRates, ratesLoading, fromCurrency, toCurrency]);

    return (
    <Box sx={{
      width: '100%',
      p: { xs: 1, sm: 2, md: 3 },
      maxWidth: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
      minWidth: 0
    }}>


      <Paper sx={{ 
        p: { xs: 2, sm: 3 }, 
        backgroundColor: 'rgb(17, 25, 39)', 
        color: 'white',
        borderRadius: 2,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        boxSizing: 'border-box'
      }}>
        {/* Верхняя валюта (Откуда) */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" sx={{ 
            color: 'rgba(255, 255, 255, 0.7)', 
            mb: 1,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}>
            {t('amount')}
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr auto auto' },
            gap: { xs: 1, sm: 1.5 }, 
            alignItems: 'center',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <TextField
              value={fromAmount}
              onChange={(e) => handleFromAmountChange(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6366f1',
                  },
                },
              }}
            />
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              textAlign: { xs: 'left', sm: 'right' },
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.75rem' },
              display: { xs: 'block', sm: 'block' },
              minWidth: { sm: 120 },
              maxWidth: { sm: 150 },
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {t('balance')}: {getWalletBalance(fromCurrency).toFixed(8)} {fromCurrency}
            </Typography>
            <FormControl sx={{ 
              minWidth: { xs: '100%', sm: 140 },
              width: { xs: '100%', sm: 'auto' },
              maxWidth: { sm: 160 }
            }}>
              <Select
                value={fromCurrency}
                onChange={(e) => handleCurrencyChange('from', e.target.value)}
                size="small"
                sx={{
                  color: 'white',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6366f1',
                  },
                }}
              >
                {Object.keys(cryptoIcons).map((currency) => (
                  <MenuItem key={currency} value={currency} sx={{ color: 'black' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img src={cryptoIcons[currency]} alt={currency} style={{ width: 20, height: 20 }} />
                      {currency}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Кнопка смены валют */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, sm: 3 } }}>
          <IconButton 
            onClick={handleSwapCurrencies}
            size="small"
            sx={{ 
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              '&:hover': {
                backgroundColor: 'rgba(99, 102, 241, 0.2)',
              }
            }}
          >
            <SwapVertIcon sx={{ 
              color: '#6366f1',
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }} />
          </IconButton>
        </Box>

        {/* Нижняя валюта (Куда) */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" sx={{ 
            color: 'rgba(255, 255, 255, 0.7)', 
            mb: 1,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}>
            {t('amount')}
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr auto auto' },
            gap: { xs: 1, sm: 1.5 }, 
            alignItems: 'center',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <TextField
              value={toAmount}
              variant="outlined"
              disabled
              size="small"
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                },
              }}
            />
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              textAlign: { xs: 'left', sm: 'right' },
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.75rem' },
              display: { xs: 'block', sm: 'block' },
              minWidth: { sm: 120 },
              maxWidth: { sm: 150 },
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {t('balance')}: {getWalletBalance(toCurrency).toFixed(8)} {toCurrency}
            </Typography>
            <FormControl sx={{ 
              minWidth: { xs: '100%', sm: 140 },
              width: { xs: '100%', sm: 'auto' },
              maxWidth: { sm: 160 }
            }}>
              <Select
                value={toCurrency}
                onChange={(e) => handleCurrencyChange('to', e.target.value)}
                size="small"
                sx={{
                  color: 'white',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6366f1',
                  },
                }}
              >
                {Object.keys(cryptoIcons).map((currency) => (
                  <MenuItem key={currency} value={currency} sx={{ color: 'black' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img src={cryptoIcons[currency]} alt={currency} style={{ width: 20, height: 20 }} />
                      {currency}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Детали транзакции */}
        <Box sx={{ 
          mb: { xs: 2, sm: 3 }, 
          p: { xs: 1.5, sm: 2 }, 
          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
          borderRadius: 1,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
            gap: { xs: 0.5, sm: 2 },
            alignItems: 'center',
            mb: { xs: 0.5, sm: 1 },
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              {t('fee')}:
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'white', 
              textAlign: { xs: 'left', sm: 'right' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              {fromCurrency} {fee.toFixed(8)} (${(fee * (exchangeRates[fromCurrency] || 0)).toFixed(2)})
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
            gap: { xs: 0.5, sm: 2 },
            alignItems: 'center',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              {t('totalAmount')}:
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'white', 
              textAlign: { xs: 'left', sm: 'right' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              {toCurrency} {totalAmount.toFixed(8)} (${(totalAmount * (exchangeRates[toCurrency] || 0)).toFixed(2)})
            </Typography>
          </Box>
        </Box>

        {/* Индикатор загрузки курсов */}
        {ratesLoading && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: 2,
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.875rem'
          }}>
            <CircularProgress size={16} />
            Updating exchange rates...
          </Box>
        )}

        {/* Кнопка обмена */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleSwap}
          disabled={isLoading || ratesLoading}
          size="large"
          sx={{
            backgroundColor: '#6366f1',
            color: 'white',
            py: { xs: 1, sm: 1.5 },
            fontSize: { xs: '0.875rem', sm: '1rem' },
            fontWeight: 'bold',
            textTransform: 'uppercase',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: '#4f46e5',
            },
            '&:active': {
              backgroundColor: '#4338ca',
            },
            '&:disabled': {
              backgroundColor: 'rgba(99, 102, 241, 0.5)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          {isLoading ? 'Processing...' : t('swap')}
        </Button>
      </Paper>
      
      {/* Уведомления */}
      <Snackbar
        open={showAlert}
        autoHideDuration={6000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowAlert(false)} 
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
}